import { Alert } from '../Alert';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { navItems } from '../../constants/sideBar';
import { useCurrentUser, useTotalUnreadCount } from '../../hooks';
import { useOrgPath } from '../../hooks/useOrgPath';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { EOrganizationMemberRole } from '../../types';
import { EOrganizationPlan } from '../../types/enum';
import { isInternalOrganizationPlan } from '../../utils/organizationPlan';
import { isOrganizationFeatureEnabled } from '../../utils/organizationFeature';
import { buildEntryUrl } from '../../utils/buildEntryUrl';
import {
  authStrings,
  headerStrings,
  sidebarStrings,
} from '../../constants/strings';
import { branding } from '../../constants/branding';
import { SideBarNavItem } from './NavItem';
import { SideBarSettingsSection } from './SettingsSection';
import { sidebarClasses } from './styles';

export type SideBarContentProps = {
  /** ログイン中のメール。ログアウトの上に出す（上部バーに出せないスマホのドロワー用） */
  email?: string;
  error?: string;
  isSigningOut: boolean;
  onSignOut: () => void;
};

export function SideBarContent({
  email,
  error,
  isSigningOut,
  onSignOut,
}: SideBarContentProps) {
  const { data: currentUser } = useCurrentUser();
  const isAdmin = currentUser?.role === EOrganizationMemberRole.ADMIN;
  const isLightPlan = currentUser?.organizationPlan === EOrganizationPlan.LIGHT;
  const { data: totalUnreadCount = 0 } = useTotalUnreadCount();
  const isInternalPlan = isInternalOrganizationPlan(
    currentUser?.organizationPlan,
  );
  const { status: copyStatus, copy } = useCopyToClipboard();
  const orgPathFor = useOrgPath();
  const entryUrl = currentUser?.organizationEntryKey
    ? buildEntryUrl(currentUser.organizationEntryKey)
    : undefined;
  const visibleNavItems = navItems.filter(
    (item) =>
      (!item.requiresAdmin || isAdmin) &&
      (!isLightPlan || item.key !== 'chat') &&
      (!item.requiresInternalPlan || isInternalPlan) &&
      (!item.requiresFeature ||
        isOrganizationFeatureEnabled(currentUser, item.requiresFeature)),
  );

  return (
    <>
      <div className={sidebarClasses.header}>
        <div className={sidebarClasses.brand}>
          <img
            src={branding.logoUrl}
            alt={branding.serviceName}
            className="h-auto w-full max-w-[200px]"
          />
        </div>
      </div>

      <div className={sidebarClasses.menuSection} data-testid="sidebar-menu">
        <span className={sidebarClasses.menuLabel}>
          {sidebarStrings.menuLabel}
        </span>
        <nav className="grid gap-2">
          {visibleNavItems.map((item) => (
            <SideBarNavItem
              key={item.key}
              item={item}
              to={item.orgScoped ? orgPathFor(item.to) : undefined}
              badgeCount={item.key === 'chat' ? totalUnreadCount : undefined}
            />
          ))}
          <SideBarSettingsSection isAdmin={isAdmin} />
        </nav>
      </div>

      <div className={sidebarClasses.footer}>
        {entryUrl && (
          <button
            type="button"
            onClick={() => copy(entryUrl)}
            className="flex w-full items-center justify-center gap-2 rounded-[var(--btn-radius)] bg-accent px-4 py-3 text-sm font-semibold text-white shadow-accent transition hover:bg-accent-mid lg:hidden"
          >
            <Icon
              icon={copyStatus === 'success' ? 'check' : 'link'}
              className="h-4 w-4"
            />
            <span>
              {copyStatus === 'success'
                ? headerStrings.copyEntryUrlSuccess
                : headerStrings.copyEntryUrl}
            </span>
          </button>
        )}
        {error && <Alert>{error}</Alert>}
        {email && (
          <p className="truncate text-center text-xs text-muted">{email}</p>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onSignOut}
          disabled={isSigningOut}
          className="uppercase tracking-[0.2em]"
        >
          {isSigningOut ? authStrings.signingOut : authStrings.signOut}
        </Button>
      </div>
    </>
  );
}
